<template>
  <div>
    <!-- Summary -->
    <b-card class="shadow-sm rounded-lg mb-2">
      <b-row class="text-center">
        <b-col
          v-for="stat in stats"
          :key="stat.key"
          cols="6"
          md="2"
          class="mb-1 mb-md-0"
        >
          <small class="text-muted d-block">{{ stat.label }}</small>
          <h5
            class="mb-0"
            :class="stat.class"
          >
            <b-spinner
              v-if="stat.loading"
              small
            />
            <template v-else>
              {{ stat.value }}
            </template>
          </h5>
        </b-col>
      </b-row>
      <template v-if="agreedAmount > 0">
        <b-progress
          :value="Math.min(collected, agreedAmount)"
          :max="agreedAmount"
          variant="success"
          height="8px"
          class="mt-1"
        />
        <small
          v-if="collected > agreedAmount"
          class="text-warning d-block mt-50"
        >
          المحصّل يتجاوز المتفق عليه بـ {{ formatCurrency(collected - agreedAmount) }}
        </small>
      </template>
    </b-card>

    <!-- Filters -->
    <b-card class="p-0 filter-card rounded-lg">
      <b-row>
        <b-col cols="12">
          <h4 class="mb-1">
            معاملات المشروع
          </h4>
        </b-col>
        <b-col
          md="3"
          class="mb-1"
        >
          <b-form-input
            v-model="filter.keyword"
            placeholder="بحث برقم المعاملة أو الوصف أو العميل"
          />
        </b-col>
        <b-col
          md="3"
          class="mb-1"
        >
          <v-select
            v-model="filter.direction"
            :options="directionOptions"
            :reduce="val => val.value"
            label="text"
            :dir="dir"
            placeholder="النوع"
            clearable
          />
        </b-col>
        <b-col
          md="3"
          class="mb-1"
        >
          <v-select
            v-model="filter.kind"
            :options="kindOptions"
            :reduce="val => val.value"
            label="text"
            :dir="dir"
            placeholder="الحساب / المحفظة"
            clearable
          />
        </b-col>
        <b-col
          md="3"
          class="mb-1"
        >
          <v-select
            v-model="filter.customer_id"
            :options="projectCustomers"
            :reduce="val => val.id"
            :get-option-label="customerLabel"
            :dir="dir"
            :loading="loadingProjectCustomers"
            placeholder="العميل / المالك"
            clearable
          />
        </b-col>
        <b-col
          md="3"
          class="mb-1"
        >
          <v-select
            v-model="filter.status"
            :options="statusOptions"
            :reduce="val => val.value"
            :get-option-label="optionLabel"
            :dir="dir"
            placeholder="الحالة"
            clearable
          />
        </b-col>
        <b-col
          md="3"
          class="mb-1"
        >
          <b-form-input
            v-model="filter.date_from"
            type="date"
            placeholder="من تاريخ"
          />
        </b-col>
        <b-col
          md="3"
          class="mb-1"
        >
          <b-form-input
            v-model="filter.date_to"
            type="date"
            placeholder="إلى تاريخ"
          />
        </b-col>
        <b-col
          md="3"
          class="mb-1"
        >
          <b-button
            variant="outline-secondary"
            block
            @click="resetFilter"
          >
            <feather-icon
              icon="XIcon"
              class="mr-50"
            />
            إعادة تعيين
          </b-button>
        </b-col>
      </b-row>
    </b-card>

    <!-- List -->
    <b-card class="w-100 rounded-lg">
      <div class="d-flex justify-content-between align-items-center pb-2">
        <small class="text-muted">
          {{ filteredRows.length }} معاملة
        </small>
        <b-dropdown
          variant="primary"
          right
          text="إضافة معاملة"
        >
          <b-dropdown-item @click="openForm('income')">
            <feather-icon
              icon="ArrowDownCircleIcon"
              class="mr-50 text-success"
            />
            سند قبض
          </b-dropdown-item>
          <b-dropdown-item @click="openForm('expense')">
            <feather-icon
              icon="ArrowUpCircleIcon"
              class="mr-50 text-danger"
            />
            سند صرف
          </b-dropdown-item>
        </b-dropdown>
      </div>

      <div
        v-if="loadingList"
        class="text-center py-5"
      >
        <b-spinner
          variant="primary"
          label="جاري التحميل..."
        />
      </div>

      <b-table
        v-else
        :items="pagedRows"
        :fields="fields"
        striped
        responsive
        show-empty
        empty-text="لا توجد معاملات"
      >
        <template #cell(transaction_number)="data">
          <b-link
            :to="{ name: 'accounting-transactions-view', params: { id: data.item.id } }"
            class="font-weight-bold"
          >
            {{ data.item.transaction_number || `#${data.item.id}` }}
          </b-link>
        </template>
        <template #cell(transaction_date)="data">
          {{ formatDate(data.item.transaction_date) }}
        </template>
        <template #cell(direction)="data">
          <b-badge :variant="data.item.direction === 'income' ? 'light-success' : 'light-danger'">
            {{ data.item.direction === 'income' ? 'سند قبض' : 'سند صرف' }}
          </b-badge>
        </template>
        <template #cell(transaction_reason)="data">
          {{ getTransactionReasonLabel(data.item.transaction_reason) }}
          <small
            v-if="data.item.description"
            class="d-block text-muted text-truncate"
            style="max-width: 220px;"
            :title="data.item.description"
          >
            {{ data.item.description }}
          </small>
        </template>
        <template #cell(customer)="data">
          {{ data.item.customer ? customerLabel(data.item.customer) : '-' }}
        </template>
        <template #cell(fund_source)="data">
          <span v-if="fundSourceLine(data.item)">{{ fundSourceLine(data.item) }}</span>
        </template>
        <template #cell(paid_from)="data">
          {{ paidFromText(data.item) }}
        </template>
        <template #cell(received_by)="data">
          {{ receivedByText(data.item) }}
        </template>
        <template #cell(source)="data">
          <b-badge
            :variant="kindBadgeVariant(data.item.kind)"
            class="mr-50"
          >
            {{ kindBadgeText(data.item.kind) }}
          </b-badge>
          <b-link
            v-if="sourceRoute(data.item)"
            :to="sourceRoute(data.item)"
          >
            {{ sourceLabel(data.item) }}
          </b-link>
          <span v-else>{{ sourceLabel(data.item) }}</span>
        </template>
        <template #cell(amount)="data">
          <span
            class="font-weight-bold text-nowrap"
            :class="data.item.direction === 'income' ? 'text-success' : 'text-danger'"
          >
            {{ data.item.direction === 'income' ? '+' : '-' }} {{ formatCurrency(data.item.amount) }}
          </span>
        </template>
        <template #cell(fee)="data">
          {{ formatCurrency(data.item.fee || 0) }}
        </template>
        <template #cell(invoice)="data">
          <b-link
            v-if="invoiceOf(data.item)"
            :href="invoiceOf(data.item).url || invoiceOf(data.item).download_url"
            target="_blank"
          >
            {{ invoiceOf(data.item).original_name || invoiceOf(data.item).file_name || 'فاتورة' }}
          </b-link>
          <span v-else>—</span>
        </template>
        <template #cell(status)="data">
          <b-badge :variant="getStatusVariant(data.item.status)">
            {{ getStatusLabel(data.item.status) }}
          </b-badge>
        </template>
        <template #cell(actions)="data">
          <div class="d-flex align-items-center">
            <b-link
              v-if="hasReceipt(data.item)"
              v-b-tooltip.hover.top="'عرض الإيصال'"
              class="ml-1 text-info"
              @click="showReceiptModal(data.item)"
            >
              <feather-icon
                icon="EyeIcon"
                size="18"
              />
            </b-link>
            <template v-if="isPendingStatus(data.item.status)">
              <b-link
                v-if="hasPermission('approve transactions')"
                v-b-tooltip.hover.top="'تأكيد'"
                class="ml-1 text-success"
                @click="changeStatus('approve', data.item.id)"
              >
                <feather-icon
                  icon="CheckIcon"
                  size="18"
                />
              </b-link>
              <b-link
                v-if="hasPermission('reject transactions')"
                v-b-tooltip.hover.top="'رفض'"
                class="ml-1 text-danger"
                @click="changeStatus('reject', data.item.id)"
              >
                <feather-icon
                  icon="XIcon"
                  size="18"
                />
              </b-link>
            </template>
          </div>
        </template>
      </b-table>

      <b-pagination
        v-if="filteredRows.length > perPage"
        v-model="currentPage"
        align="center"
        :total-rows="filteredRows.length"
        :per-page="perPage"
        class="mt-2"
      />
    </b-card>

    <!-- Create Transaction Modal -->
    <b-modal
      id="transaction-modal"
      ref="transactionModal"
      :title="isIncome ? 'سند قبض' : 'سند صرف'"
      size="lg"
      @hidden="resetForm"
    >
      <validation-observer ref="transactionForm">
        <b-form @submit.prevent="saveTransaction">
          <div class="text-center mb-2">
            <b-form-radio-group
              v-model="form.direction"
              :options="directionOptions"
              :button-variant="isIncome ? 'outline-success' : 'outline-danger'"
              buttons
              @change="onDirectionChange"
            />
          </div>
          <b-row>
            <receiver-chart-fields
              :key="`fund-${receiverFormKey}`"
              :value="form.fund_gl_account_id"
              :label="$t('accounting_paid_from')"
              input-id="form_fund_gl_account_id"
              :blocked-id="form.receiver_gl_account_id"
              :excluded-ids="excludedChartIds"
              :server-error="localizedFieldError('fund_gl_account_id')"
              @input="onFundChart"
            />
            <b-col
              v-if="!isIncome"
              md="6"
            >
              <validation-provider
                #default="{ errors }"
                name="transaction_reason"
                rules="required"
              >
                <b-form-group
                  label="سبب المعاملة"
                  label-for="form_transaction_reason"
                >
                  <v-select
                    id="form_transaction_reason"
                    v-model="form.transaction_reason"
                    :options="expenseTransactionReasons"
                    :reduce="val => val.value"
                    :get-option-label="optionLabel"
                    :dir="dir"
                    :loading="loadingExpenseReasons"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>
            <receiver-chart-fields
              :key="`receiver-${receiverFormKey}`"
              :value="form.receiver_gl_account_id"
              :label="$t('accounting_received_by')"
              input-id="form_receiver_gl_account_id"
              :blocked-id="form.fund_gl_account_id"
              :excluded-ids="excludedChartIds"
              :server-error="localizedFieldError('receiver_gl_account_id')"
              @input="onReceiverChart"
            />
            <b-col md="6">
              <validation-provider
                #default="{ errors }"
                name="amount"
                rules="required|min_value:0.01"
              >
                <b-form-group
                  label="المبلغ"
                  label-for="form_amount"
                >
                  <b-form-input
                    id="form_amount"
                    v-model="form.amount"
                    type="number"
                    step="0.01"
                    min="0.01"
                    @input="autoAmount = false"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                  <small
                    v-if="feeBlocked"
                    class="text-danger d-block"
                  >
                    العمولة لا تتجاوز المبلغ.
                  </small>
                  <small
                    v-if="isIncome && agreedAmount > 0"
                    class="text-muted d-block"
                  >
                    المتبقي للتحصيل {{ formatCurrency(remaining) }}
                  </small>
                  <b-alert
                    :show="excessAmount > 0"
                    variant="warning"
                    class="mt-50 mb-0 p-50 small"
                  >
                    المبلغ يتجاوز المتبقي من المتفق عليه بـ {{ formatCurrency(excessAmount) }}
                  </b-alert>
                </b-form-group>
              </validation-provider>
            </b-col>
            <b-col md="6">
              <b-form-group
                label="العمولة"
                label-for="form_fee"
              >
                <b-form-input
                  id="form_fee"
                  v-model.number="form.fee"
                  type="number"
                  step="0.01"
                  min="0"
                />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group
                label="ملف الفاتورة"
                label-for="form_invoice_file"
              >
                <b-form-file
                  id="form_invoice_file"
                  ref="invoiceInput"
                  :placeholder="form.invoice_file ? form.invoice_file.name : 'اختر ملف'"
                  browse-text="تصفح"
                  accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                  @change="onInvoiceChange"
                />
              </b-form-group>
            </b-col>
            <b-col
              v-if="moneyPreview"
              cols="12"
            >
              <b-alert
                show
                :variant="moneyPreview.feeExceedsAmount ? 'danger' : (moneyPreview.negative ? 'warning' : 'light')"
                class="mb-1"
              >
                <div v-if="isIncome">
                  المبلغ الداخل = المبلغ − الرسوم
                </div>
                <div v-else>
                  المبلغ الخارج = المبلغ + الرسوم
                  <span v-if="moneyPreview.fee > 0">العمولة تُرحّل على 5340.</span>
                </div>
                <div v-if="moneyPreview.nextBalance != null">
                  الرصيد بعد الحفظ: {{ formatCurrency(moneyPreview.nextBalance) }}
                </div>
              </b-alert>
            </b-col>
            <b-col md="6">
              <validation-provider
                #default="{ errors }"
                name="transaction_date"
                rules="required"
              >
                <b-form-group
                  label="تاريخ المعاملة"
                  label-for="form_transaction_date"
                >
                  <b-form-input
                    id="form_transaction_date"
                    v-model="form.transaction_date"
                    type="date"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>
            <b-col md="6">
              <b-form-group
                label="إيصال"
                label-for="form_receipt_file"
              >
                <b-form-file
                  id="form_receipt_file"
                  ref="receiptInput"
                  :placeholder="form.receipt_file ? form.receipt_file.name : 'اختر ملف'"
                  :browse-text="$i18n.locale === 'ar' ? 'تصفح' : 'Browse'"
                  accept="image/*,.pdf"
                  @change="onReceiptChange"
                />
                <small class="text-muted d-block mt-1">
                  {{ form.receipt_file ? form.receipt_file.name : 'PDF أو صورة (حد أقصى 10MB)' }}
                </small>
              </b-form-group>
            </b-col>
            <b-col md="12">
              <validation-provider
                #default="{ errors }"
                name="description"
                rules="max:1000"
              >
                <b-form-group
                  label="الوصف"
                  label-for="form_description"
                >
                  <b-form-textarea
                    id="form_description"
                    v-model="form.description"
                    rows="3"
                    maxlength="1000"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>
          </b-row>
          <small class="text-muted">
            <feather-icon
              icon="InfoIcon"
              class="mr-50"
            />
            تُحفظ المعاملة قيد الانتظار حتى يؤكدها المسؤول.
          </small>
        </b-form>
      </validation-observer>
      <template #modal-footer="{ cancel }">
        <b-button
          variant="secondary"
          @click="cancel()"
        >
          إلغاء
        </b-button>
        <b-button
          :variant="isIncome ? 'success' : 'danger'"
          :disabled="saving || feeBlocked"
          @click="saveTransaction"
        >
          <b-spinner
            v-if="saving"
            small
            class="mr-1"
          />
          حفظ
        </b-button>
      </template>
    </b-modal>

    <!-- View Receipt Modal -->
    <b-modal
      id="view-receipt-modal"
      ref="viewReceiptModal"
      :title="'عرض الإيصال'"
      size="xl"
      centered
      @hidden="currentReceiptUrl = null"
    >
      <div
        v-if="currentReceiptUrl"
        class="receipt-viewer"
      >
        <div
          v-if="isImageFile(currentReceiptUrl)"
          class="text-center"
        >
          <img
            :src="currentReceiptUrl"
            alt="Receipt"
            class="img-fluid"
            style="max-height: 70vh; width: auto;"
          >
        </div>
        <div
          v-else-if="isPdfFile(currentReceiptUrl)"
          class="pdf-viewer"
        >
          <iframe
            :src="currentReceiptUrl"
            frameborder="0"
            style="width: 100%; height: 70vh;"
          />
        </div>
        <div
          v-else
          class="text-center py-5"
        >
          <p class="text-muted">
            لا يمكن عرض هذا النوع من الملفات
          </p>
          <b-button
            :href="currentReceiptUrl"
            target="_blank"
            variant="primary"
          >
            <feather-icon
              icon="DownloadIcon"
              class="mr-1"
            />
            تحميل الملف
          </b-button>
        </div>
      </div>
      <div
        v-else
        class="text-center py-5"
      >
        <b-spinner variant="primary" />
      </div>
      <template #modal-footer="{ ok }">
        <b-button
          variant="secondary"
          @click="ok()"
        >
          إغلاق
        </b-button>
        <b-button
          v-if="currentReceiptUrl"
          :href="currentReceiptUrl"
          target="_blank"
          variant="primary"
        >
          <feather-icon
            icon="DownloadIcon"
            class="mr-1"
          />
          تحميل
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BTable,
  BButton,
  BDropdown,
  BDropdownItem,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BFormFile,
  BFormRadioGroup,
  BPagination,
  BProgress,
  BAlert,
  BSpinner,
  BBadge,
  BModal,
  BLink,
  VBTooltip,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import vSelect from 'vue-select'
import FeatherIcon from '@/@core/components/feather-icon/FeatherIcon.vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import * as accountsAPI from '@/api/accounting/accounts'
import * as walletsAPI from '@/api/accounting/wallets'
import * as transactionReasonsAPI from '@/api/accounting/transactionReasons'
import * as financialAPI from '@/api/projects/projectFinancial'
import { feeExceedsAmount, invoiceFileError, invoiceMedia, moneyEffect } from '@/utils/transactionMoney'
import {
  drawerChartIds, fetchActivePayouts, fieldError, fundSourceParts,
  paidFromText, payoutOptionLabel, receivedByText, voucherErrorMessage, voucherSourceText, walletOptionLabel,
} from '@/utils/voucherSource'
import ProjectsAPI from '@/api/projects/projects'
import { collectPages } from '@/utils/ledger'
import ReceiverChartFields from '../accounting/ReceiverChartFields.vue'

const FUND_ERROR_KEYS = {
  'Cash cannot be received from the same account it is deposited into.': 'accounting_fund_same_account',
  'Chart account is inactive or does not accept posting.': 'accounting_fund_gl_inactive',
  'This chart account belongs to a cash, bank, or wallet account. Choose that account instead.': 'accounting_fund_gl_till',
  'Receiving chart account is inactive or does not accept posting.': 'accounting_receiver_inactive',
}

const MAX_RECEIPT_SIZE = 10 * 1024 * 1024
const RECEIPT_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'application/pdf']
const PAGE_SIZE = 100

const SOURCES = [
  { fetch: financialAPI.getIncomeTransactions, direction: 'income', kind: 'account' },
  { fetch: financialAPI.getCashoutTransactions, direction: 'expense', kind: 'account' },
  { fetch: financialAPI.getCustodyExpenses, direction: 'expense', kind: 'wallet' },
]

const today = () => new Date().toISOString().split('T')[0]

const emptyFilter = () => ({
  keyword: null,
  direction: null,
  kind: null,
  customer_id: null,
  status: null,
  date_from: null,
  date_to: null,
})

const emptyForm = (direction = 'income') => ({
  direction,
  source_key: null,
  transaction_reason: null,
  amount: null,
  fee: 0,
  fund_gl_account_id: null,
  receiver_gl_account_id: null,
  description: '',
  receipt_file: null,
  invoice_file: null,
  transaction_date: today(),
})

const toNumber = value => {
  const n = parseFloat(String(value ?? '').replace(/,/g, ''))
  return Number.isFinite(n) ? n : 0
}

const enumOf = value => (value && typeof value === 'object' ? value.value : value)

export default {
  name: 'ProjectFinancialData',
  components: {
    BCard,
    BRow,
    BCol,
    BTable,
    BButton,
    BDropdown,
    BDropdownItem,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BFormFile,
    BFormRadioGroup,
    BPagination,
    BProgress,
    BAlert,
    BSpinner,
    BBadge,
    BModal,
    BLink,
    ValidationProvider,
    ValidationObserver,
    vSelect,
    FeatherIcon,
    ReceiverChartFields,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  props: {
    projectId: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      fields: [
        { key: 'transaction_number', label: 'رقم المعاملة' },
        { key: 'transaction_date', label: 'التاريخ' },
        { key: 'direction', label: 'النوع' },
        { key: 'transaction_reason', label: 'السبب' },
        { key: 'customer', label: 'العميل' },
        { key: 'fund_source', label: 'مصدر المبلغ' },
        { key: 'paid_from', label: 'دفع من' },
        { key: 'received_by', label: 'استلم في' },
        { key: 'source', label: 'الحساب / المحفظة' },
        { key: 'amount', label: 'المبلغ' },
        { key: 'fee', label: 'العمولة' },
        { key: 'invoice', label: 'الفاتورة' },
        { key: 'status', label: 'الحالة' },
        { key: 'actions', label: 'الإجراءات' },
      ],
      statusOptions: [
        { value: 'pending', label: 'Pending', label_ar: 'قيد الانتظار' },
        { value: 'approved', label: 'Approved', label_ar: 'معتمد' },
        { value: 'rejected', label: 'Rejected', label_ar: 'مرفوض' },
        { value: 'cancelled', label: 'Cancelled', label_ar: 'ملغي' },
      ],
      directionOptions: [
        { value: 'income', text: 'سند قبض' },
        { value: 'expense', text: 'سند صرف' },
      ],
      kindOptions: [
        { value: 'account', text: 'حساب الشركة' },
        { value: 'wallet', text: 'محفظة عهدة' },
        { value: 'employee_payment_account', text: 'حساب صرف الموظف' },
      ],
      filter: emptyFilter(),
      search: '',
      searchTimeout: null,
      rows: [],
      requestId: 0,
      loadingList: false,
      currentPage: 1,
      perPage: 15,
      accounts: [],
      wallets: [],
      payoutAccounts: [],
      projectCustomers: [],
      fieldErrors: {},
      expenseTransactionReasons: [],
      loadingAccounts: false,
      loadingWallets: false,
      loadingPayouts: false,
      loadingProjectCustomers: false,
      loadingExpenseReasons: false,
      collected: 0,
      loadingCollected: false,
      form: emptyForm(),
      receiverFormKey: 0,
      autoAmount: false,
      saving: false,
      currentReceiptUrl: null,
    }
  },
  computed: {
    dir() {
      return this.$store.state.appConfig.layout.isRTL ? 'rtl' : 'ltr'
    },
    project() {
      return this.$store.getters['projects/project'] || {}
    },
    agreedAmount() {
      if (this.project.id && String(this.project.id) !== String(this.projectId)) return 0
      return toNumber(this.project.actual_cost)
    },
    remaining() {
      return Math.max(0, this.agreedAmount - this.collected)
    },
    serverFilterKey() {
      return [this.filter.status, this.filter.date_from, this.filter.date_to].join('|')
    },
    filteredRows() {
      const { direction, kind, customer_id: customerId } = this.filter
      const term = this.search.trim().toLowerCase()
      return this.rows.filter(row => {
        if (direction && row.direction !== direction) return false
        if (kind && row.kind !== kind) return false
        if (customerId && String(row.customer?.id ?? row.customer_id) !== String(customerId)) return false
        if (!term) return true
        return [row.transaction_number, row.description, row.customer && this.customerLabel(row.customer)]
          .some(text => text && String(text).toLowerCase().includes(term))
      })
    },
    pagedRows() {
      const start = (this.currentPage - 1) * this.perPage
      return this.filteredRows.slice(start, start + this.perPage)
    },
    totals() {
      return this.filteredRows.reduce((acc, row) => {
        const status = enumOf(row.status)
        if (status === 'pending') acc.pending += 1
        if (status !== 'rejected' && status !== 'cancelled') acc[row.direction] += toNumber(row.amount)
        return acc
      }, { income: 0, expense: 0, pending: 0 })
    },
    stats() {
      const hasAgreed = this.agreedAmount > 0
      return [
        {
          key: 'agreed', label: 'المتفق عليه', value: hasAgreed ? this.formatCurrency(this.agreedAmount) : 'لم يُحدد', class: hasAgreed ? '' : 'text-muted',
        },
        {
          key: 'collected', label: 'المحصّل', value: this.formatCurrency(this.collected), class: 'text-success', loading: this.loadingCollected,
        },
        {
          key: 'remaining', label: 'المتبقي', value: hasAgreed ? this.formatCurrency(this.remaining) : '-', class: this.remaining > 0 ? 'text-danger' : 'text-success',
        },
        {
          key: 'income', label: 'إجمالي سندات القبض', value: this.formatCurrency(this.totals.income), class: 'text-success', loading: this.loadingList,
        },
        {
          key: 'expense', label: 'إجمالي سندات الصرف', value: this.formatCurrency(this.totals.expense), class: 'text-danger', loading: this.loadingList,
        },
        {
          key: 'pending', label: 'بانتظار التأكيد', value: this.totals.pending, class: 'text-warning', loading: this.loadingList,
        },
      ]
    },
    isIncome() {
      return this.form.direction === 'income'
    },
    excessAmount() {
      if (!this.isIncome || this.agreedAmount <= 0) return 0
      return Math.max(0, toNumber(this.form.amount) - this.remaining)
    },
    excludedChartIds() {
      return drawerChartIds([...(this.accounts || []), ...(this.wallets || []), ...(this.payoutAccounts || [])])
    },
    sourceOptions() {
      const accounts = this.accounts.map(a => ({
        key: `account-${a.id}`, kind: 'account', id: a.id, label: this.getAccountLabel(a), balance: a.current_balance,
      }))
      const wallets = this.wallets.map(w => ({
        key: `wallet-${w.id}`, kind: 'wallet', id: w.id, label: walletOptionLabel(w), balance: w.current_balance,
      }))
      const payouts = this.payoutAccounts.map(row => ({
        key: `payout-${row.id}`, kind: 'employee_payment_account', id: row.id, label: payoutOptionLabel(row), balance: null,
      }))
      return [...accounts, ...wallets, ...payouts]
    },
    feeBlocked() {
      return feeExceedsAmount(this.isIncome ? 'income' : 'expense', this.form.amount, this.form.fee)
    },
    moneyPreview() {
      if (!this.form.amount && !this.form.fee) return null
      return moneyEffect({
        direction: this.isIncome ? 'income' : 'expense',
        amount: this.form.amount,
        fee: this.form.fee,
        balance: this.selectedSource ? this.selectedSource.balance : null,
      })
    },
    selectedSource() {
      return this.sourceOptions.find(o => o.key === this.form.source_key) || null
    },
  },
  watch: {
    projectId: {
      immediate: true,
      handler(newVal) {
        if (!newVal) return
        this.refreshAll()
        this.loadProjectCustomers()
      },
    },
    'filter.keyword': function onKeyword(value) {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => { this.search = value || '' }, 500)
    },
    serverFilterKey() {
      this.loadTransactions()
    },
    filteredRows() {
      const lastPage = Math.max(1, Math.ceil(this.filteredRows.length / this.perPage))
      if (this.currentPage > lastPage) this.currentPage = 1
    },
  },
  mounted() {
    this.loadAccounts()
    this.loadWallets()
    this.loadPayouts()
  },
  beforeDestroy() {
    clearTimeout(this.searchTimeout)
  },
  methods: {
    notify(variant, text) {
      const titles = { success: 'نجح', warning: 'تحذير', danger: 'خطأ' }
      this.$toast({
        component: ToastificationContent,
        props: {
          title: titles[variant],
          icon: variant === 'success' ? 'CheckIcon' : 'AlertTriangleIcon',
          variant,
          text,
        },
      })
    },
    errorText(error, fallback) {
      return voucherErrorMessage(error, error?.message || fallback)
    },
    kindBadgeText(kind) {
      if (kind === 'wallet') return 'محفظة عهدة'
      if (kind === 'employee_payment_account') return 'حساب صرف الموظف'
      return 'حساب الشركة'
    },
    kindBadgeVariant(kind) {
      if (kind === 'wallet') return 'light-warning'
      if (kind === 'employee_payment_account') return 'light-info'
      return 'light-primary'
    },
    refreshAll() {
      this.loadTransactions()
      this.loadCollected()
    },
    serverParams() {
      const { status, date_from: dateFrom, date_to: dateTo } = this.filter
      const params = { per_page: PAGE_SIZE }
      if (status) params.status = status
      if (dateFrom) params.date_from = dateFrom
      if (dateTo) params.date_to = dateTo
      return params
    },
    async loadTransactions() {
      if (!this.projectId) return
      this.loadingList = true
      const params = this.serverParams()
      this.requestId += 1
      const { requestId } = this
      try {
        const lists = await Promise.all(SOURCES.map(async source => {
          const first = await source.fetch(this.projectId, { ...params, page: 1 })
          const items = await collectPages(page => source.fetch(this.projectId, { ...params, page }), first)
          return items.map(item => this.normalizeRow(item, source))
        }))
        if (requestId !== this.requestId) return
        const byId = new Map()
        lists.forEach(list => list.forEach(row => { if (!byId.has(row.id)) byId.set(row.id, row) }))
        this.rows = [...byId.values()].sort((a, b) => (
          String(b.transaction_date || '').localeCompare(String(a.transaction_date || '')) || b.id - a.id
        ))
      } catch (error) {
        console.error('Error loading project transactions:', error)
        this.notify('danger', this.errorText(error, 'تعذر تحميل معاملات المشروع'))
      } finally {
        if (requestId === this.requestId) this.loadingList = false
      }
    },
    normalizeRow(item, source) {
      const type = enumOf(item.transaction_type)
      let { direction } = source
      if (type) direction = type === 'income' ? 'income' : 'expense'
      return {
        ...item,
        direction,
        kind: enumOf(item.payment_source) || source.kind,
        _rowVariant: enumOf(item.status) === 'pending' ? 'warning' : null,
      }
    },
    resetFilter() {
      this.filter = emptyFilter()
      this.search = ''
    },
    async loadCollected() {
      if (!this.projectId) return
      this.loadingCollected = true
      try {
        const response = await financialAPI.getIncomeTransactions(this.projectId, { status: 'approved', per_page: 1 })
        this.collected = toNumber(response?.summary?.total_amount)
      } catch (error) {
        console.error('Error loading collected amount:', error)
        this.collected = 0
      } finally {
        this.loadingCollected = false
      }
    },
    async loadAccounts() {
      this.loadingAccounts = true
      try {
        const response = await accountsAPI.getAccounts({ status: 'active', per_page: 1000 })
        this.accounts = response.data || []
      } catch (error) {
        console.error('Error loading accounts:', error)
        this.accounts = []
      } finally {
        this.loadingAccounts = false
      }
    },
    async loadPayouts() {
      this.loadingPayouts = true
      try {
        this.payoutAccounts = await fetchActivePayouts()
      } catch (error) {
        console.error('Error loading payout accounts:', error)
        this.payoutAccounts = []
      } finally {
        this.loadingPayouts = false
      }
    },
    async loadWallets() {
      this.loadingWallets = true
      try {
        const response = await walletsAPI.getWallets({ status: 'active', per_page: 1000 })
        this.wallets = response.data || []
      } catch (error) {
        console.error('Error loading wallets:', error)
        this.wallets = []
      } finally {
        this.loadingWallets = false
      }
    },
    async loadProjectCustomers() {
      this.loadingProjectCustomers = true
      try {
        const response = await new ProjectsAPI().getProjectCustomers(this.projectId)
        const list = Array.isArray(response) ? response : response?.data
        this.projectCustomers = Array.isArray(list) ? list : []
      } catch (error) {
        console.error('Error loading project customers:', error)
        this.projectCustomers = []
      } finally {
        this.loadingProjectCustomers = false
      }
    },
    async loadExpenseTransactionReasons() {
      this.loadingExpenseReasons = true
      try {
        const isArabic = this.$i18n.locale === 'ar'
        const response = isArabic
          ? await transactionReasonsAPI.getExpenseTransactionReasonOptionsAr()
          : await transactionReasonsAPI.getExpenseTransactionReasonOptions()

        let reasons = []
        if (Array.isArray(response)) {
          reasons = response
        } else if (Array.isArray(response?.data)) {
          reasons = response.data
        } else if (response?.data && typeof response.data === 'object') {
          // /options endpoints return { key: label } in one language; fetch the other for both labels
          let englishLabels = isArabic ? {} : response.data
          let arabicLabels = isArabic ? response.data : {}
          try {
            const other = isArabic
              ? await transactionReasonsAPI.getExpenseTransactionReasonOptions()
              : await transactionReasonsAPI.getExpenseTransactionReasonOptionsAr()
            if (other?.data && typeof other.data === 'object' && !Array.isArray(other.data)) {
              if (isArabic) englishLabels = other.data
              else arabicLabels = other.data
            }
          } catch (e) {
            console.error('Error loading translated transaction reasons:', e)
          }
          const keys = new Set([...Object.keys(englishLabels), ...Object.keys(arabicLabels)])
          reasons = Array.from(keys).map(key => ({
            value: key,
            label: englishLabels[key] || key,
            label_ar: arabicLabels[key] || key,
          }))
        }
        this.expenseTransactionReasons = reasons
      } catch (error) {
        console.error('Error loading expense transaction reasons:', error)
        this.expenseTransactionReasons = []
      } finally {
        this.loadingExpenseReasons = false
      }
    },
    openForm(direction) {
      this.form = emptyForm(direction)
      this.receiverFormKey += 1
      this.prepareDirection()
      this.$refs.transactionModal.show()
    },
    onDirectionChange() {
      if (!this.isIncome && this.autoAmount) {
        this.form.amount = null
        this.autoAmount = false
      }
      this.prepareDirection()
    },
    prepareDirection() {
      if (!this.isIncome) {
        if (!this.expenseTransactionReasons.length) this.loadExpenseTransactionReasons()
        return
      }
      if (!this.form.amount && this.remaining > 0) {
        this.form.amount = this.remaining
        this.$nextTick(() => { this.autoAmount = true })
      }
    },
    fundSourceLine(item) {
      const parts = fundSourceParts(item)
      return parts ? this.$t(parts.key, parts.params) : ''
    },
    paidFromText,
    receivedByText,
    localizedFieldError(field) {
      const raw = this.fieldErrors[field] || ''
      if (!raw) return ''
      const key = FUND_ERROR_KEYS[raw]
      return key ? this.$t(key) : raw
    },
    onFundChart(id) {
      this.form.fund_gl_account_id = id
      if (this.fieldErrors) this.fieldErrors.fund_gl_account_id = ''
    },
    onReceiverChart(id) {
      this.form.receiver_gl_account_id = id
      if (this.fieldErrors) this.fieldErrors.receiver_gl_account_id = ''
    },
    invoiceOf(item) {
      return invoiceMedia(item)
    },
    onInvoiceChange(event) {
      const file = event?.target?.files ? event.target.files[0] : null
      if (file && invoiceFileError(file)) {
        this.notify('danger', 'الفاتورة يجب أن تكون PDF أو JPG أو JPEG أو PNG وبحد أقصى 10MB')
        if (this.$refs.invoiceInput) this.$refs.invoiceInput.reset()
        this.form.invoice_file = null
        return
      }
      this.form.invoice_file = file
    },
    onReceiptChange(event) {
      const file = event?.target?.files ? event.target.files[0] : null
      let error = null
      if (file && file.size > MAX_RECEIPT_SIZE) error = 'حجم الملف يجب أن يكون أقل من 10MB'
      else if (file && !RECEIPT_TYPES.includes(file.type)) error = 'نوع الملف غير مدعوم. يرجى اختيار صورة أو ملف PDF'
      if (error) {
        this.notify('danger', error)
        this.$refs.receiptInput.reset()
        this.form.receipt_file = null
        return
      }
      this.form.receipt_file = file
    },
    buildPayload() {
      const { form, isIncome } = this
      const fields = {
        transaction_type: form.direction,
        transaction_reason: isIncome ? null : form.transaction_reason,
        amount: form.amount,
        fee: form.fee || 0,
        description: form.description,
        transaction_date: form.transaction_date,
        status: 'pending',
        fund_gl_account_id: form.fund_gl_account_id,
        receiver_gl_account_id: form.receiver_gl_account_id,
      }
      Object.keys(fields).forEach(key => {
        if (fields[key] === null || fields[key] === undefined || fields[key] === '') delete fields[key]
      })
      const hasReceipt = form.receipt_file instanceof File
      const hasInvoice = form.invoice_file instanceof File
      if (!hasReceipt && !hasInvoice) return fields

      const data = new FormData()
      Object.keys(fields).forEach(key => data.append(key, fields[key]))
      if (hasReceipt) data.append('receipt', form.receipt_file)
      if (hasInvoice) data.append('invoice', form.invoice_file)
      return data
    },
    async saveTransaction() {
      const isValid = await this.$refs.transactionForm.validate()
      if (!isValid || this.feeBlocked || !this.form.fund_gl_account_id || !this.form.receiver_gl_account_id) {
        this.notify('warning', this.feeBlocked ? 'العمولة لا تتجاوز المبلغ.' : 'يرجى التحقق من صحة البيانات المدخلة')
        return
      }
      this.saving = true
      try {
        await financialAPI.createTransaction(this.projectId, this.buildPayload())
        this.notify('success', 'تم حفظ المعاملة وهي بانتظار التأكيد')
        this.$refs.transactionModal.hide()
        this.refreshAll()
      } catch (error) {
        this.fieldErrors = {
          fund_gl_account_id: fieldError(error, 'fund_gl_account_id'),
          receiver_gl_account_id: fieldError(error, 'receiver_gl_account_id'),
        }
        this.notify('danger', this.errorText(error, 'حدث خطأ أثناء إنشاء المعاملة'))
      } finally {
        this.saving = false
      }
    },
    resetForm() {
      this.form = emptyForm()
      this.receiverFormKey += 1
      this.autoAmount = false
      if (this.$refs.transactionForm) this.$refs.transactionForm.reset()
      if (this.$refs.receiptInput) this.$refs.receiptInput.reset()
      if (this.$refs.invoiceInput) this.$refs.invoiceInput.reset()
    },
    async changeStatus(action, transactionId) {
      const approving = action === 'approve'
      try {
        if (approving) await financialAPI.approveTransaction(this.projectId, transactionId)
        else await financialAPI.rejectTransaction(this.projectId, transactionId)
        this.notify('success', approving ? 'تم تأكيد المعاملة بنجاح' : 'تم رفض المعاملة بنجاح')
        this.refreshAll()
      } catch (error) {
        this.notify('danger', this.errorText(error, approving
          ? 'حدث خطأ أثناء تأكيد المعاملة'
          : 'حدث خطأ أثناء رفض المعاملة'))
      }
    },
    optionLabel(option) {
      return this.$i18n.locale === 'ar' ? option.label_ar : option.label
    },
    customerLabel(customer) {
      return customer?.name || customer?.full_name || ''
    },
    getAccountLabel(account) {
      if (!account) return ''
      return account.account_number ? `${account.account_number} - ${account.name}` : account.name
    },
    formatCurrency(amount, currency = 'EGP') {
      return new Intl.NumberFormat('ar-EG', {
        style: 'currency',
        currency,
      }).format(toNumber(amount))
    },
    formatDate(date) {
      if (!date) return ''
      return new Date(date).toLocaleDateString('ar-EG')
    },
    getStatusVariant(status) {
      const variants = {
        pending: 'warning',
        approved: 'success',
        rejected: 'danger',
        cancelled: 'secondary',
      }
      return variants[enumOf(status)] || 'secondary'
    },
    getStatusLabel(status) {
      if (status && typeof status === 'object') return this.optionLabel(status)
      const option = this.statusOptions.find(opt => opt.value === status)
      return option ? this.optionLabel(option) : status
    },
    getTransactionReasonLabel(transactionReason) {
      if (!transactionReason) return '-'
      if (typeof transactionReason === 'object') return this.optionLabel(transactionReason)
      return transactionReason
    },
    sourceLabel(row) {
      return voucherSourceText(row, this.payoutAccounts) || this.getSourceLabel(row.source)
    },
    sourceRoute(row) {
      if (row.kind === 'employee_payment_account') return null
      return row.kind === 'wallet' ? this.getWalletSourceRoute(row.source) : this.getSourceRoute(row.source)
    },
    getSourceLabel(source) {
      if (!source) return '-'
      if (source.wallet_number) return this.getWalletSourceLabel(source)
      if (source.account_number) return `${source.account_number} - ${source.name || ''}`
      if (source.code) return `${source.code} - ${source.name || ''}`
      return source.name || '-'
    },
    getSourceRoute(source) {
      if (!source) return null
      if (source.wallet_number) return this.getWalletSourceRoute(source)
      return source.id ? { name: 'accounting-accounts-view', params: { id: source.id } } : null
    },
    getWalletSourceLabel(source) {
      return walletOptionLabel(source) || '-'
    },
    getWalletSourceRoute(source) {
      if (source?.employee?.id) {
        return { name: 'employeeDetails', params: { id: source.employee.id } }
      }
      return null
    },
    hasPermission(permission) {
      const userPermissions = this.$store.getters['users/user_permissions']
      if (Array.isArray(userPermissions)) {
        return userPermissions.includes(permission)
      }
      return Object.values(userPermissions || {}).includes(permission)
    },
    isPendingStatus(status) {
      return enumOf(status) === 'pending'
    },
    receiptPathOf(transaction) {
      if (!transaction) return null
      if (Array.isArray(transaction.receipt) && transaction.receipt[0]?.url) return transaction.receipt[0].url
      if (transaction.receipt?.url) return transaction.receipt.url
      return transaction.receipt_path || null
    },
    hasReceipt(transaction) {
      return !!this.receiptPathOf(transaction)
    },
    getReceiptUrl(receiptPath) {
      if (!receiptPath) return ''
      if (receiptPath.startsWith('http://') || receiptPath.startsWith('https://')) {
        return receiptPath
      }
      const cleanPath = receiptPath.startsWith('/') ? receiptPath : `/${receiptPath}`
      if (receiptPath.startsWith('/storage') || receiptPath.startsWith('storage/')) {
        return `${process.env.VUE_APP_BASE_URL}${cleanPath}`
      }
      return `${process.env.VUE_APP_BASE_URL}/storage${cleanPath}`
    },
    showReceiptModal(transaction) {
      const receiptPath = this.receiptPathOf(transaction)
      this.currentReceiptUrl = receiptPath ? this.getReceiptUrl(receiptPath) : null
      this.$refs.viewReceiptModal.show()
    },
    isImageFile(url) {
      if (!url) return false
      const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp']
      const lowerUrl = url.toLowerCase()
      return imageExtensions.some(ext => lowerUrl.includes(ext))
        || lowerUrl.includes('image/')
    },
    isPdfFile(url) {
      if (!url) return false
      const lowerUrl = url.toLowerCase()
      return lowerUrl.includes('.pdf') || lowerUrl.includes('application/pdf')
    },
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
